import { getCurrentCompanyId } from '../utils/auth';
import { reportsAPI, invoicesAPI, expensesAPI, generalLedgerAPI } from '../lib/api';

// Mock data fallback for dashboard metrics
const mockDashboardData = {
  kpis: {
    totalRevenue: 125000,
    totalExpenses: 85000,
    netProfit: 40000,
    cashFlow: 15000,
    accountsReceivable: 25000,
    accountsPayable: 18000
  },
  recentActivity: [
    {
      id: 1,
      type: 'invoice',
      description: 'Invoice INV-2026-001 created',
      amount: 5500,
      date: '2026-01-15T10:00:00Z'
    },
    {
      id: 2,
      type: 'payment',
      description: 'Payment received from Tech Solutions Inc.',
      amount: 3200,
      date: '2026-01-14T14:30:00Z'
    }
  ],
  chartData: {
    revenue: [
      { month: 'Jan', amount: 12000 },
      { month: 'Feb', amount: 15000 },
      { month: 'Mar', amount: 18000 },
      { month: 'Apr', amount: 16000 },
      { month: 'May', amount: 20000 },
      { month: 'Jun', amount: 22000 }
    ],
    expenses: [
      { month: 'Jan', amount: 8000 },
      { month: 'Feb', amount: 9500 },
      { month: 'Mar', amount: 11000 },
      { month: 'Apr', amount: 10500 },
      { month: 'May', amount: 12000 },
      { month: 'Jun', amount: 13500 }
    ]
  }
};

/**
 * Get dashboard data with company currency formatting
 * @param {string} companyId - Company ID
 * @param {string} currency - Company currency code
 * @returns {Promise<object>} Dashboard data with formatted currency values
 */
export const getDashboardDataWithCurrency = async (companyId = null, currency = null) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      console.warn('No company ID available, using mock data');
      return mockDashboardData;
    }
    
    // Get KPIs and recent activity
    const [kpis, recentActivity, cashFlowData] = await Promise.allSettled([
      getKPIs(actualCompanyId),
      getRecentActivity(actualCompanyId),
      getCashFlowData(actualCompanyId)
    ]);
    
    // Format currency values
    const formatCurrency = (amount) => {
      try {
        if (!currency) return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount || 0);
      } catch (error) {
        console.warn('Invalid currency code:', currency);
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
      }
    };
    
    const kpiData = kpis.status === 'fulfilled' ? kpis.value : {
      totalRevenue: 0,
      totalExpenses: 0,
      netProfit: 0,
      cashFlow: 0,
      accountsReceivable: 0,
      accountsPayable: 0
    };
    
    return {
      kpis: {
        ...kpiData,
        totalRevenueFormatted: formatCurrency(kpiData.totalRevenue),
        totalExpensesFormatted: formatCurrency(kpiData.totalExpenses),
        netProfitFormatted: formatCurrency(kpiData.netProfit),
        cashFlowFormatted: formatCurrency(kpiData.cashFlow),
        accountsReceivableFormatted: formatCurrency(kpiData.accountsReceivable),
        accountsPayableFormatted: formatCurrency(kpiData.accountsPayable)
      },
      recentActivity: recentActivity.status === 'fulfilled' ? recentActivity.value : [],
      chartData: cashFlowData.status === 'fulfilled' ? cashFlowData.value : [],
      currency
    };
  } catch (error) {
    console.error('Error in getDashboardDataWithCurrency:', error);
    return mockDashboardData;
  }
};

/**
 * Get comprehensive dashboard data from the new API endpoint
 * @param {string} companyId - Company ID
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<object>} Complete dashboard data
 */
export const getDashboardData = async (companyId = null, startDate = null, endDate = null) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      console.warn('No company ID available');
      return null;
    }

    // Default to current year if no dates provided
    if (!startDate || !endDate) {
      const now = new Date();
      startDate = `${now.getFullYear()}-01-01`;
      endDate = `${now.getFullYear()}-12-31`;
    }

    const response = await reportsAPI.getDashboard(actualCompanyId, { startDate, endDate });
    
    if (response.data && response.data.Success && response.data.Data) {
      const data = response.data.Data;
      
      // Parse JSON strings from the API
      return {
        periodInfo: JSON.parse(data.PeriodInfo || '{}'),
        financialMetrics: JSON.parse(data.FinancialMetrics || '{}'),
        invoiceBreakdown: JSON.parse(data.InvoiceBreakdown || '[]'),
        recentTransactions: JSON.parse(data.RecentTransactions || '[]'),
        topCustomers: JSON.parse(data.TopCustomers || '[]'),
        customerDetails: JSON.parse(data.CustomerDetails || '[]'),
        monthlyTrends: JSON.parse(data.MonthlyTrends || '[]'),
        accountSummary: JSON.parse(data.AccountSummary || '[]'),
        recentJournalEntries: JSON.parse(data.RecentJournalEntries || '[]'),
        expenseByCategory: data.ExpenseByCategory ? JSON.parse(data.ExpenseByCategory) : null,
        agingReceivables: JSON.parse(data.AgingReceivables || '{}'),
        dailyActivity: JSON.parse(data.DailyActivity || '[]'),
        creditStatusSummary: JSON.parse(data.CreditStatusSummary || '[]'),
        customerCategorySummary: JSON.parse(data.CustomerCategorySummary || '[]'),
        additionalKpis: JSON.parse(data.AdditionalKpis || '{}'),
        systemOverview: JSON.parse(data.SystemOverview || '{}')
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    return null;
  }
};

export const getKPIs = async (companyId = null) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    console.log('Dashboard service - Company ID:', actualCompanyId);
    
    if (!actualCompanyId) {
      console.warn('No company ID available, returning empty KPIs');
      return {
        totalRevenue: 0,
        totalExpenses: 0,
        netProfit: 0,
        cashFlow: 0,
        accountsReceivable: 0,
        accountsPayable: 0
      };
    }
    
    // Try to get data from new dashboard endpoint
    const dashboardData = await getDashboardData(actualCompanyId);
    
    if (dashboardData && dashboardData.financialMetrics) {
      const metrics = dashboardData.financialMetrics;
      const cashFlowData = dashboardData.additionalKpis?.cash_flow_summary 
        ? JSON.parse(dashboardData.additionalKpis.cash_flow_summary) 
        : {};
      
      return {
        totalRevenue: parseFloat(metrics.total_invoiced || 0),
        totalExpenses: parseFloat(metrics.total_expenses || 0),
        netProfit: parseFloat(metrics.total_invoiced || 0) - parseFloat(metrics.total_expenses || 0),
        cashFlow: parseFloat(cashFlowData.net_cashflow || 0),
        accountsReceivable: parseFloat(metrics.outstanding_receivables || 0),
        accountsPayable: 0, // Not in current API
        totalCollected: parseFloat(metrics.total_collected || 0),
        overdueAmount: parseFloat(metrics.overdue_amount || 0),
        activeCustomers: parseInt(metrics.active_customers || 0),
        totalAssets: parseFloat(metrics.total_assets || 0),
        totalLiabilities: parseFloat(metrics.total_liabilities || 0),
        totalEquity: parseFloat(metrics.total_equity || 0)
      };
    }
    
    // Fallback to empty data
    return {
      totalRevenue: 0,
      totalExpenses: 0,
      netProfit: 0,
      cashFlow: 0,
      accountsReceivable: 0,
      accountsPayable: 0
    };
  } catch (error) {
    console.error('Error getting KPIs:', error);
    return {
      totalRevenue: 0,
      totalExpenses: 0,
      netProfit: 0,
      cashFlow: 0,
      accountsReceivable: 0,
      accountsPayable: 0
    };
  }
};

export const getRecentActivity = async (companyId = null) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      console.warn('No company ID available, returning empty activity');
      return [];
    }
    
    // Try to get data from new dashboard endpoint
    const dashboardData = await getDashboardData(actualCompanyId);
    
    if (dashboardData && dashboardData.recentTransactions) {
      return dashboardData.recentTransactions.map(transaction => ({
        id: transaction.reference_number,
        type: transaction.transaction_type.toLowerCase(),
        description: `${transaction.party_name}`,
        amount: parseFloat(transaction.amount || 0),
        date: transaction.transaction_date,
        status: transaction.status,
        partyEmail: transaction.party_email,
        customerId: transaction.customer_id
      }));
    }
    
    return [];
  } catch (error) {
    console.error('Error getting recent activity:', error);
    return [];
  }
};

export const getChartData = async (companyId = null, type = 'revenue') => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      console.warn('No company ID available, returning empty chart data');
      return [];
    }
    
    // TODO: Replace with actual API call to get real chart data
    // For now, return empty array for new companies
    console.log('Returning empty chart data for new company');
    return [];
  } catch (error) {
    console.error('Error getting chart data:', error);
    return [];
  }
};

export const getCashFlowData = async (companyId = null) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      console.warn('No company ID available, returning empty cash flow data');
      return [];
    }
    
    // Try to get data from new dashboard endpoint
    const dashboardData = await getDashboardData(actualCompanyId);
    
    if (dashboardData && dashboardData.monthlyTrends) {
      return dashboardData.monthlyTrends.map(trend => ({
        month: trend.month_name,
        income: parseFloat(trend.total_credits || 0),
        expenses: parseFloat(trend.total_debits || 0),
        profit: parseFloat(trend.net_balance || 0)
      }));
    }
    
    return [];
  } catch (error) {
    console.error('Error getting cash flow data:', error);
    return [];
  }
};

export const getExpenseBreakdown = async (companyId = null) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      console.warn('No company ID available, returning empty expense breakdown');
      return [];
    }
    
    // Check if we should use mock data for development
    const useMockData = import.meta.env.VITE_USE_MOCK_DATA === 'true';
    
    if (useMockData) {
      // Mock expense breakdown data
      return [
        { name: 'Payroll', value: 35000, color: 'var(--color-primary)' },
        { name: 'Rent', value: 12000, color: 'var(--color-secondary)' },
        { name: 'Utilities', value: 8000, color: 'var(--color-accent)' },
        { name: 'Marketing', value: 15000, color: 'var(--color-success)' },
        { name: 'Supplies', value: 5000, color: 'var(--color-warning)' }
      ];
    }
    
    // TODO: Replace with actual API call to get real expense breakdown
    // For new companies, return empty array
    console.log('Returning empty expense breakdown for new company');
    return [];
  } catch (error) {
    console.error('Error getting expense breakdown:', error);
    return [];
  }
};

export const getRevenueTrend = async (companyId = null) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      console.warn('No company ID available, returning empty revenue trend');
      return [];
    }
    
    // Check if we should use mock data for development
    const useMockData = import.meta.env.VITE_USE_MOCK_DATA === 'true';
    
    if (useMockData) {
      // Mock revenue trend data
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
      return months.map(month => ({
        month,
        revenue: Math.floor(Math.random() * 25000) + 15000
      }));
    }
    
    // TODO: Replace with actual API call to get real revenue trend
    // For new companies, return empty array
    console.log('Returning empty revenue trend for new company');
    return [];
  } catch (error) {
    console.error('Error getting revenue trend:', error);
    return [];
  }
};